import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { LoaderType } from '@fcom/common/interfaces';
import { CartStatus, FrequentFlyerCard, PaxDetailsFormData, Phone } from '@fcom/dapi';
import { isString, unsubscribe } from '@fcom/core/utils';
import { FinnairCart } from '@fcom/dapi/api/models';
import { NotificationTheme } from '@fcom/ui-components';
import { DataCloudService } from '@fcom/common/datacloud';
import { ConfigService } from '@fcom/core/';
import { CommonBookingState } from '@fcom/common-booking';
import { SeatMapActions } from '@fcom/common-booking/store';

import { paxDetails } from '../../../store/selectors';

@Component({
  selector: 'fin-pax-details',
  templateUrl: 'pax-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PaxDetailsComponent implements OnInit, OnDestroy {
  public readonly LoaderType = LoaderType;
  public readonly NotificationTheme = NotificationTheme;

  @Input()
  cartStatus$: Observable<CartStatus>;

  @Input()
  cartData$: Observable<FinnairCart>;

  @Output()
  ready: EventEmitter<{ skipToCheckout: boolean }> = new EventEmitter<{ skipToCheckout: boolean }>();

  @Output()
  unlockTravelingWith: EventEmitter<boolean> = new EventEmitter<boolean>();

  infoMessageKey$: BehaviorSubject<string> = new BehaviorSubject(undefined);

  enableTicketTypeUpselling: boolean;

  private subscription = new Subscription();

  constructor(
    private configService: ConfigService,
    private store$: Store<CommonBookingState>,
    private dataCloudService: DataCloudService
  ) {}

  ngOnInit(): void {
    this.enableTicketTypeUpselling = this.configService.cfg.enableTicketTypeUpselling;

    // Clear the seatmap when returning to pax details in case passengers have changed
    this.store$.dispatch(SeatMapActions.reset());
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscription);
  }

  setInfoMessage(messageKey = ''): void {
    if (isString(messageKey)) {
      this.infoMessageKey$.next(messageKey);
    }
  }

  emitReady({ skipToCheckout }: { skipToCheckout: boolean }): void {
    this.subscription.add(
      this.store$.pipe(paxDetails(), take(1)).subscribe((paxDetailState) => {
        const paxDetailsFormData: PaxDetailsFormData = paxDetailState.passengers[0];
        const phone: Phone = paxDetailsFormData.phone;
        const frequentFlyerCard: FrequentFlyerCard = paxDetailsFormData.frequentFlyerCard;
        this.dataCloudService.paxDetails({
          firstName: paxDetailsFormData.firstName,
          lastName: paxDetailsFormData.lastName,
          emailAddress: paxDetailsFormData.email,
          countryCode: phone?.phonePrefix,
          phoneNumber: phone?.phoneNumber,
          memberProgram: frequentFlyerCard?.companyCode,
          memberNumber: frequentFlyerCard?.cardNumber,
        });
      })
    );
    this.ready.emit({ skipToCheckout });
  }
}
